import React, { useState, ChangeEvent, useEffect } from 'react';
import { FormControlLabel, TextField, Checkbox } from '@material-ui/core';
import { IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { isEmpty, get } from 'lodash';

import SahamDrawer from 'components/SahamDrawer';
import { Config } from 'containers/AppContainer/typings';
import { translate } from 'containers/services/translate';
import messages from 'containers/RecapManagerPage/messages';
import {
  OTHER_REASON_ID,
  FRAUDE_CORPO,
} from 'containers/RecapManagerPage/constants';
import {
  EMPTY_REJECTION_DECISION,
  REJECTION_DECISION,
} from 'containers/data-structure';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import { FEATURES } from 'features/features';
import currentUserStorage from 'containers/services/security/current-user-storage';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import * as featureService from 'features/fflip-helpers';
import { validateRejectionReasons } from 'containers/RecapManagerPage/utils';

export interface RejectionDrawerProps {
  intl: IntlShape;
  open: boolean;
  handleToggleDrawer: any;
  rejectionReasons: Array<Config>;
  claimId: number;
  requestRejectClaim: Function;
}

const Container = styled.div`
  .fields {
    display: flex;
    flex-direction: column;
    margin-top: 0px;
  }
  .input {
    width: 327px;
    margin-bottom: 33px;
    margin-left: 24px;
    margin-right: 24px;
  }
  .MuiFormLabel-root {
    font-size: 13px;
    color: #4f4f4f;
  }
  .MuiInputLabel-shrink {
    color: #00000061;
  }
  .MuiFormLabel-root.Mui-focused {
    color: rgba(9, 122, 194, 1);
  }
  .MuiFormControlLabel {
    width: 327px;
    margin-left: 15px;
  }
  .MuiTypography-body1 {
    font-size: 13px;
  }
`;

const RejectionDrawer: React.FC<RejectionDrawerProps> = (
  props,
): JSX.Element => {
  const [decision, updateDecision] = useState(EMPTY_REJECTION_DECISION) as any;
  useEffect(() => {
    updateDecision({
      ...EMPTY_REJECTION_DECISION,
      [REJECTION_DECISION.REJECTION_REASON]: new Set([]),
    });
  }, []);

  const handleChange = (field: string) => (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    const { value } = event.target;
    let expectedDecision: any = { ...decision };
    if (checked) {
      expectedDecision[REJECTION_DECISION.REJECTION_REASON].add(value);
    } else {
      expectedDecision[REJECTION_DECISION.REJECTION_REASON].delete(value);

      formatOtherReason(expectedDecision);
    }
    updateDecision(expectedDecision);
  };

  const handleChangeOther = (event: any) => {
    const { value } = event.target;
    let expectedDecision: any = { ...decision };
    expectedDecision[REJECTION_DECISION.OTHER_REASON] = value;
    updateDecision(expectedDecision);
  };

  const formatOtherReason = (rejectionDecision: any) => {
    if (
      !rejectionDecision[REJECTION_DECISION.REJECTION_REASON].has(
        OTHER_REASON_ID,
      )
    ) {
      rejectionDecision[REJECTION_DECISION.OTHER_REASON] = '';
    }

    return rejectionDecision;
  };

  const handleValidateDrawer = () => {
    props.requestRejectClaim(
      props.claimId,
      [...get(decision, [REJECTION_DECISION.REJECTION_REASON])],
      get(decision, [REJECTION_DECISION.OTHER_REASON]),
      {
        decidedBy: currentUserStorage.get().username.toLowerCase(),
        decidedAt: moment().format(),
      },
      handleRejectSuccessCallback,
    );
    updateDecision({
      ...EMPTY_REJECTION_DECISION,
      [REJECTION_DECISION.REJECTION_REASON]: new Set([]),
    });
  };

  /* istanbul ignore next */
  const handleRejectSuccessCallback = () => {
    if (!featureService.hasFeature(FEATURES.FEE_NOTE)) {
      toast.success(translate(messages.rejectSuccessMessage, props));
      updateDecision({
        ...EMPTY_REJECTION_DECISION,
        [REJECTION_DECISION.REJECTION_REASON]: new Set([]),
      });
      pushTo(PAGES.MANAGER_HOME)();
    } else {
      toast.success(translate(messages.rejectSuccessMessage, props));
      updateDecision({
        ...EMPTY_REJECTION_DECISION,
        [REJECTION_DECISION.REJECTION_REASON]: new Set([]),
      });
    }
  };

  return (
    <SahamDrawer
      open={props.open}
      handleToggleDrawer={props.handleToggleDrawer}
      title={translate({ ...messages.managerRecapPageRejectionTitle }, props)}
      handleValidateDrawer={handleValidateDrawer}
      confirmButtonTitle={translate(messages.drawerConfirmButtonTitle, props)}
      isSaveButtonDisabled={validateRejectionReasons(decision)}
    >
      <Container>
        <div className="fields">
          {!isEmpty(props.rejectionReasons) &&
            props.rejectionReasons.map((reason: any) => (
              <FormControlLabel
                key={reason.id}
                className="MuiFormControlLabel"
                control={
                  <Checkbox
                    color="primary"
                    value={reason.id}
                    onChange={handleChange(REJECTION_DECISION.REJECTION_REASON)}
                    className="checkbox"
                  />
                }
                label={reason.title}
              />
            ))}
          {[...get(decision, [REJECTION_DECISION.REJECTION_REASON])].includes(
            OTHER_REASON_ID,
          ) && (
            <TextField
              id="other-reason"
              data-testid="other-reason-field"
              name={REJECTION_DECISION.OTHER_REASON}
              onChange={handleChangeOther}
              value={get(decision, [REJECTION_DECISION.OTHER_REASON], '')}
              label={translate(
                messages.managerRecapPageRejectionOtherReason,
                props,
              )}
              className="input"
              multiline
            />
          )}
          {[...get(decision, [REJECTION_DECISION.REJECTION_REASON])].includes(
            FRAUDE_CORPO,
          )}
        </div>
      </Container>
    </SahamDrawer>
  );
};

export default injectIntl(RejectionDrawer);
